import {ColumnDef} from "../ColumnDef";
import {DATE, DATE_LINK, NUMBER} from "../../constants";
import {Button, Tooltip} from "@mui/material";
import React from "react";
import {Row} from "../Row";
import {CheckCircle, Pending} from "@mui/icons-material";

export class GroupBy extends ColumnDef {

    constructor({column}) {
        super({
            displayName: column.displayName,
            options: {...column.options},
            index: column.getIndex(),
            type: column.type || NUMBER,
            style: {...column.style},
        });

        this.column = column; // The column that is being grouped by
    }

    toJSON() {
        return {
            ...super.toJSON(),
            className: this.constructor.name,
            column: this.column.toJSON(),
            id: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15), // Unique ID for this instance
        };
    }

    static fromJSON(json) {
        return new GroupBy({
            column: ColumnDef.fromJSON(json.column),
        });
    }

    getIndex() {
        return this.column.getIndex();
    }

    static group(rows, groupBys) {
        const orderedGroupKeys = [];
        const observedGroupKeys = new Set();
        const groups = new Map();
        for (const row of rows) {
            const groupKey = JSON.stringify(groupBys.map(g => {
                const cell = row.cells[g.getIndex()];
                const sortValue = cell.getSortValue();
                const columnDef = cell.columnDef;
                if (columnDef.type === DATE || columnDef.type === DATE_LINK) {
                    return new Date(sortValue).toLocaleDateString("en-US");
                }
                return sortValue;
            }));
            if (!observedGroupKeys.has(groupKey)) {
                observedGroupKeys.add(groupKey);
                orderedGroupKeys.push(groupKey);
            }
            if (!groups.has(groupKey)) {
                groups.set(groupKey, []);
            }
            groups.get(groupKey).push(row);
        }
        return {groups, orderedGroupKeys};
    }


    static apply(view) {
        const groupBys = view.getPendingGroupBys();
        const aggregatedColumns = view.getPendingAggregations();
        const state = view.getState();

        if (!groupBys || groupBys.length === 0) {
            return;
        }
        if (!aggregatedColumns || aggregatedColumns.length === 0) {
            return;
        }

        const {groups, orderedGroupKeys} = GroupBy.group(view.getViewableRows(), groupBys);
        const groupedRows = [];

        if (!orderedGroupKeys || orderedGroupKeys.length === 0) {
            console.warn("No groups found, returning original state.");
            return;
        }

        for (const groupKey of orderedGroupKeys) {
            const group = groups.get(groupKey);
            const firstRow = group[0];
            if (!firstRow) {
                console.warn("No rows found for group key:", groupKey);
                continue;
            }
            const groupCells = groupBys.map(col => firstRow.cells[col.getIndex()]);
            const aggregatedCells = aggregatedColumns.map(col => firstRow.cells[col.getIndex()]);

            const row = new Row([
                ...groupCells,
                ...aggregatedCells
            ], groupedRows.length);

            groupedRows.push(row);
        }

        view.setState({
            ...state,
            rows: groupedRows,
            orderedGroupKeys: orderedGroupKeys,
        });
    }

    renderPending(view) {
        return <div
            role="active-view-option-row"
            className="active-view-option-row"
            key={this.key()}>
            <span
                className="active-view-option-row-label"
                role="pending-group-by"
            >
                <Tooltip slotProps={{
                    popper: { // Render the tooltip on top of everything else:
                        className: "canold-table-tooltip"
                    }
                }} title={"We are not yet grouping the table on this column. Click \"Update View\" to group after you have chosen an aggregation."}>
                    <Pending className={"canold-table-pending-icon"}/>
                </Tooltip>
                {this.column.displayName}
            </span>
            <Button
                variant="outlined"
                className="pending-group-by-remove-button"
                role="pending-group-by-remove-button"
                onClick={() => view.removePendingStateChange(this)}
            >
                Remove
            </Button>
        </div>
    }

    renderApplied(view) {
        return <div
            role="active-view-option-row"
            className="active-view-option-row"
            key={this.key()}>
            <span
                className="active-view-option-row-label"
                role="applied-group-by"
            >
                <Tooltip slotProps={{
                    popper: { // Render the tooltip on top of everything else:
                        className: "canold-table-tooltip"
                    }
                }} title={"The table is currently grouped on this column. Click Remove or Reset Table to un-group."}>
                    <CheckCircle className={"canold-table-applied-icon"}/>
                </Tooltip>
                {this.column.displayName}
            </span>
            <Button
                variant="outlined"
                className="applied-group-by-remove-button"
                role="applied-group-by-remove-button"
                onClick={() => view.removeAppliedStateChange(this)}
            >
                Remove
            </Button>
        </div>
    }

}

export default GroupBy;